/** Native source order and MIME declarations let the browser choose a rendition. */
export function setVideoSources(video, sources) {
    video.removeAttribute('src');
    video.replaceChildren();
    for (const format of ['webm', 'mp4']) {
        if (!sources[format]) continue;
        const source = video.ownerDocument.createElement('source');
        source.dataset.videoFormat = format;
        source.type =
            sources[`${format}_type`] ||
            (format === 'webm' ? 'video/webm; codecs="vp9"' : 'video/mp4');
        source.src = sources[format];
        video.append(source);
    }
}

export function readVideoSources(video) {
    const sources = {};
    video.querySelectorAll('source[src]').forEach((source) => {
        const format =
            source.dataset.videoFormat || ((source.type || '').includes('vp9') ? 'webm' : 'mp4');
        sources[format] = source.src;
    });
    return sources;
}

/** A failed WebM may try its MP4 once; autoplay rejection is not a media error. */
export function videoMp4Fallback(current, { mp4 = '', webm = '' }) {
    return current && current === webm && mp4 && mp4 !== webm ? mp4 : '';
}

/** Start in the original click stack, before importing or animating a viewer. */
export function startVideoFromInteraction(
    container,
    sources,
    { poster = '', label = '', className = '' } = {},
) {
    const video = container.ownerDocument.createElement('video');
    video.className = className;
    video.playsInline = true;
    video.preload = 'none';
    video.poster = poster;
    video.setAttribute('aria-label', label || 'Produktvideo');
    setVideoSources(video, sources);
    container.append(video);
    video.play().catch(() => {});
    return video;
}

export function disposeVideo(video) {
    if (!video) return;
    video.pause();
    video.removeAttribute('src');
    video.replaceChildren();
    try {
        video.load();
    } catch (error) {
        // Firefox may synchronously report a cancelled media fetch during teardown.
        if (error.name !== 'AbortError') throw error;
    }
    video.remove();
}

/** Only autoplay denial permits a muted retry; aborted or stale starts stay stopped. */
export async function playVideoWithMutedFallback(video, isActive) {
    try {
        await video.play();
    } catch (error) {
        if (error.name !== 'NotAllowedError' || video.muted || !isActive()) return;
        video.muted = true;
        try {
            await video.play();
        } catch {
            // Native controls and the owning player's manual start remain available.
        }
    }
}

const initializedVideos = new WeakSet();

function initializeNativeVideo(video) {
    if (initializedVideos.has(video)) return;
    initializedVideos.add(video);
    const sources = readVideoSources(video);
    let retried = false;
    let playbackStarted = false;
    video.addEventListener('play', () => {
        playbackStarted = true;
    });
    const retryMp4 = () => {
        const fallback = videoMp4Fallback(video.currentSrc, sources);
        if (retried || !fallback || !video.isConnected) return;
        retried = true;
        video.src = fallback;
        video.load();
        if (playbackStarted || video.autoplay) video.play().catch(() => {});
    };
    video.addEventListener('error', retryMp4);
}

/** Legacy/native players keep HTML source fallbacks and never acquire sources eagerly. */
export function initVideoVariants(root = document) {
    if (root.matches?.('video[data-video-variants]')) initializeNativeVideo(root);
    root.querySelectorAll?.('video[data-video-variants]').forEach(initializeNativeVideo);
}
